/**
 * DeviceRestrictionsCard — per-device override editor for the onboarding
 * rules (required restrictions + freshness windows).
 *
 * Why per-device:
 *   The platform-wide settings on SettingsPage flip the rules for EVERY
 *   device. That's the right default for the fleet, but a single device
 *   sometimes legitimately can't satisfy the global rule — a test phone
 *   without an FRP recovery email, a sparse-cadence handset, a unit
 *   under manual forensic review. This card lets an operator override
 *   any combination of the three thresholds for ONE device only; the
 *   onboarding-status decider picks per-device > platform > defaults.
 *
 * UI shape:
 *   - Restrictions: 4 checkboxes (the closed RESTRICTION_CHOICES list).
 *     Default state = the device's currently effective value (from
 *     OnboardingStatus.effectiveRequiredPolicies). Toggling makes the
 *     "Override required policies" flag flip true so the diff is saved.
 *   - Snapshot-fresh window: number input (minutes). Blank = inherit
 *     platform default.
 *   - Tamper-fresh window: number input (hours).
 *   - Two buttons: "Save overrides" (PATCH) and "Reset to platform"
 *     (DELETE). Reset is a hard wipe of every override.
 *
 * Permission: gated by `device.write` (matches the API endpoint).
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import type { Device, OnboardingStatus } from "@dfp/api-client";
import { Button } from "./Button.js";
import { Card, CardBody, CardHeader, CardTitle } from "./Card.js";
import { useAuth } from "../auth/AuthContext.js";
import { api } from "../lib/api.js";

/**
 * The four DPC restriction keys the platform knows about. Kept in
 * sync with `RESTRICTION_CHOICES` in apps/api/src/platform-settings/settings-catalog.ts;
 * adding a key there without updating this constant means the new
 * choice won't appear as a checkbox in this card.
 */
const RESTRICTION_CHOICES = [
  { key: "frp", label: "Factory Reset Protection" },
  { key: "disallow_factory_reset", label: "Disallow factory reset" },
  { key: "disallow_debugging_features", label: "Disallow debugging features" },
  { key: "disallow_install_unknown_sources", label: "Disallow unknown-sources installs" },
] as const;

interface Props {
  readonly device: Device;
  readonly onboarding: OnboardingStatus | undefined;
}

export function DeviceRestrictionsCard({ device, onboarding }: Props): JSX.Element {
  const auth = useAuth();
  const qc = useQueryClient();
  // Same permission shape as the rest of the admin pages (see
  // DeviceDetailPage / LoanDetailPage); kept inline rather than
  // factored to a hook because the indirection isn't worth a 1-line
  // helper at this MVP scale.
  const canEdit = (auth.user?.permissions ?? []).includes("device.write");

  // The override that's currently persisted (null = no override).
  const persisted = device.restrictionOverrides ?? null;

  // Form state — mirrors persisted on mount; "isDirty" tracks whether
  // anything's been edited so the Save button stays disabled otherwise.
  const [policies, setPolicies] = useState<readonly string[] | null>(null);
  const [snapshotFreshMin, setSnapshotFreshMin] = useState<string>("");
  const [tamperFreshHr, setTamperFreshHr] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  // Reload form state whenever the device row changes (after a save
  // refetch lands). This keeps the inputs reflecting truth, not stale
  // local state.
  useEffect(() => {
    setPolicies(persisted?.requiredPolicies ?? null);
    setSnapshotFreshMin(
      persisted?.snapshotFreshMs !== undefined
        ? Math.round(persisted.snapshotFreshMs / 60_000).toString()
        : "",
    );
    setTamperFreshHr(
      persisted?.tamperFreshMs !== undefined
        ? Math.round(persisted.tamperFreshMs / 3_600_000).toString()
        : "",
    );
    setError(null);
  }, [
    device.id,
    persisted?.requiredPolicies,
    persisted?.snapshotFreshMs,
    persisted?.tamperFreshMs,
  ]);

  // Resolved current values (override OR onboarding-status fallback)
  // so we show what the decider is actually using right now.
  const effectivePolicies = useMemo(
    () =>
      policies ?? onboarding?.effectiveRequiredPolicies ?? RESTRICTION_CHOICES.map((r) => r.key),
    [policies, onboarding?.effectiveRequiredPolicies],
  );

  const save = useMutation({
    mutationFn: (body: {
      requiredPolicies?: readonly string[];
      snapshotFreshMs?: number;
      tamperFreshMs?: number;
    }) => api.patch<Device>(`/api/devices/${device.id}/restriction-overrides`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["device", device.id] });
      void qc.invalidateQueries({ queryKey: ["onboarding-status", device.id] });
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Failed to save overrides.");
    },
  });

  const reset = useMutation({
    mutationFn: () => api.delete<Device>(`/api/devices/${device.id}/restriction-overrides`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["device", device.id] });
      void qc.invalidateQueries({ queryKey: ["onboarding-status", device.id] });
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Failed to reset overrides.");
    },
  });

  function onSave(): void {
    setError(null);
    const body: {
      requiredPolicies?: readonly string[];
      snapshotFreshMs?: number;
      tamperFreshMs?: number;
    } = {};
    if (policies !== null) body.requiredPolicies = policies;
    const snapshotMs = parsePositiveMinutesToMs(snapshotFreshMin);
    if (snapshotMs !== null) body.snapshotFreshMs = snapshotMs;
    const tamperMs = parsePositiveHoursToMs(tamperFreshHr);
    if (tamperMs !== null) body.tamperFreshMs = tamperMs;
    save.mutate(body);
  }

  function togglePolicy(key: string): void {
    const current = policies ?? effectivePolicies;
    const next = current.includes(key) ? current.filter((p) => p !== key) : [...current, key];
    setPolicies(next);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          Restrictions
          {persisted !== null ? (
            <span className="ml-2 rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-blue-800">
              Custom
            </span>
          ) : null}
        </CardTitle>
      </CardHeader>
      <CardBody className="space-y-4">
        <p className="text-xs text-slate-500">
          Per-device override of the platform onboarding rules. Empty fields fall through to the
          platform default. The decider re-runs the next time GET /onboarding-status is called
          (admin page polls every 15 s).
        </p>

        <div>
          <p className="text-xs font-semibold text-slate-700">Required DPC restrictions</p>
          <div className="mt-1 space-y-1">
            {RESTRICTION_CHOICES.map((r) => (
              <label key={r.key} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={effectivePolicies.includes(r.key)}
                  onChange={() => togglePolicy(r.key)}
                  disabled={!canEdit}
                  className="h-4 w-4 rounded border-slate-300"
                />
                <span className="font-mono text-xs">{r.key}</span>
                <span className="text-slate-500">— {r.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="block text-xs">
            <span className="font-semibold text-slate-700">Snapshot freshness (min)</span>
            <input
              type="number"
              min={1}
              max={43200}
              value={snapshotFreshMin}
              onChange={(e) => setSnapshotFreshMin(e.target.value)}
              placeholder={defaultMinutesPlaceholder(onboarding?.effectiveSnapshotFreshMs)}
              disabled={!canEdit}
              className="mt-1 block w-full rounded border border-slate-300 px-2 py-1 text-sm"
            />
          </label>
          <label className="block text-xs">
            <span className="font-semibold text-slate-700">Tamper freshness (hr)</span>
            <input
              type="number"
              min={1}
              max={720}
              value={tamperFreshHr}
              onChange={(e) => setTamperFreshHr(e.target.value)}
              placeholder={defaultHoursPlaceholder(onboarding?.effectiveTamperFreshMs)}
              disabled={!canEdit}
              className="mt-1 block w-full rounded border border-slate-300 px-2 py-1 text-sm"
            />
          </label>
        </div>

        {error ? <p className="text-xs text-red-700">{error}</p> : null}

        <div className="flex gap-2">
          <Button onClick={onSave} disabled={!canEdit || save.isPending}>
            {save.isPending ? "Saving…" : "Save overrides"}
          </Button>
          {persisted !== null ? (
            <Button
              variant="ghost"
              onClick={() => reset.mutate()}
              disabled={!canEdit || reset.isPending}
            >
              {reset.isPending ? "Resetting…" : "Reset to platform default"}
            </Button>
          ) : null}
        </div>
        {!canEdit ? (
          <p className="text-[11px] text-slate-500">
            You need <code>device.write</code> permission to edit restrictions.
          </p>
        ) : null}
      </CardBody>
    </Card>
  );
}

function parsePositiveMinutesToMs(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * 60_000);
}

function parsePositiveHoursToMs(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * 3_600_000);
}

function defaultMinutesPlaceholder(ms: number | undefined): string {
  if (ms === undefined) return "platform default";
  return `${Math.round(ms / 60_000)} (platform default)`;
}

function defaultHoursPlaceholder(ms: number | undefined): string {
  if (ms === undefined) return "platform default";
  return `${Math.round(ms / 3_600_000)} (platform default)`;
}
